import type { StrictLocaleObject } from '../../shared/locales'

/**
 * Sort locales by multi-level geographic proximity
 * @param locales - Array of locale objects with border information
 * @param currentCountry - The current country code
 * @param maxDepth - Maximum levels of neighbor relationships to consider (default: 3)
 * @returns Sorted array: current → direct neighbors → 2nd level → 3rd level → others
 */
export function sortLocalesByMultiLevelProximity<T extends StrictLocaleObject>(locales: T[], currentCountry: string, maxDepth = 3): T[] {
  const localeMap = new Map(locales.map(l => [l.country, l]))

  // Build proximity levels using BFS
  const proximityLevels = new Map<string, number>()
  const visited = new Set<string>()
  const queue: { country: string, level: number }[] = [{ country: currentCountry, level: 0 }]

  while (queue.length > 0) {
    const { country, level } = queue.shift()!

    if (visited.has(country) || level > maxDepth)
      continue
    visited.add(country)

    // Set the proximity level for this country
    if (!proximityLevels.has(country)) {
      proximityLevels.set(country, level)
    }

    // Add neighbors to queue
    const locale = localeMap.get(country)
    if (locale?.borders) {
      for (const neighbor of locale.borders) {
        if (!visited.has(neighbor) && localeMap.has(neighbor)) {
          queue.push({ country: neighbor, level: level + 1 })
        }
      }
    }
  }

  // Sort locales based on proximity levels
  return locales.slice().sort((a, b) => {
    const levelA = proximityLevels.get(a.country) ?? Infinity
    const levelB = proximityLevels.get(b.country) ?? Infinity

    // First sort by proximity level
    if (levelA !== levelB) {
      return levelA - levelB
    }

    // Within the same level, sort alphabetically by country code
    return a.country.localeCompare(b.country)
  })
}

/**
 * Get detailed proximity information for all locales
 * @param locales - Array of locale objects
 * @param currentCountry - The current country code
 * @param maxDepth - Maximum depth to search
 * @returns Map of country codes to their proximity level
 */
export function getProximityLevels<T extends StrictLocaleObject>(locales: T[], currentCountry: string, maxDepth = 3): Map<string, number> {
  const localeMap = new Map(locales.map(l => [l.country, l]))
  const proximityLevels = new Map<string, number>()
  const visited = new Set<string>()
  const queue: { country: string, level: number }[] = [
    { country: currentCountry, level: 0 },
  ]

  while (queue.length > 0) {
    const { country, level } = queue.shift()!

    if (visited.has(country) || level > maxDepth)
      continue
    visited.add(country)

    proximityLevels.set(country, level)

    const locale = localeMap.get(country)
    if (locale?.borders) {
      for (const neighbor of locale.borders) {
        if (!visited.has(neighbor) && localeMap.has(neighbor)) {
          queue.push({ country: neighbor, level: level + 1 })
        }
      }
    }
  }

  return proximityLevels
}

/**
 * Simple wrapper for backwards compatibility
 * @param locales - Array of locale objects
 * @param currentCountry - The current country code
 * @returns Sorted array with advanced multi-level proximity
 */
export function sortLocalesByProximity<T extends StrictLocaleObject>(
  locales: T[],
  currentCountry: string,
): T[] {
  return sortLocalesByMultiLevelProximity(locales, currentCountry, 3)
}
